import { CheckCircle2, FileSearch, FileSpreadsheet, Filter, Plus, UploadCloud } from 'lucide-react'
import { useEffect, useMemo, useState, type DragEvent, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { normalizeDocument, normalizeDocumentsCollection } from '../api/normalize'
import type { PprDocument } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/ToastProvider'
import { Badge, Button, CachedNotice, EmptyState, ErrorState, LoadingState, Modal, Pagination, SearchField, statusTone } from '../components/ui'
import { useOfflineQuery } from '../hooks/useOfflineQuery'
import { useObjects } from '../hooks/useReferences'
import { executeOrQueueFile } from '../offline/outbox'
import { documentKindLabel, errorMessage, fileName, formatDate, formatNumber, importStatusLabel } from '../utils/format'

const MAX_FILE_SIZE = 50 * 1024 * 1024
const supportedExtensions = ['xlsx', 'xls', 'pdf']

export function ImportsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(Number(searchParams.get('page') || 1))
  const [status, setStatus] = useState(searchParams.get('status') || '')
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [appliedSearch, setAppliedSearch] = useState(search)
  const [uploadOpen, setUploadOpen] = useState(searchParams.get('upload') === '1')

  const query = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), limit: '25' })
    if (status) params.set('status', status)
    if (appliedSearch) params.set('q', appliedSearch)
    return params.toString()
  }, [appliedSearch, page, status])
  const imports = useOfflineQuery<unknown>(`imports:${query}`, `/import/ppr?${query}`, { raw: true, maxAgeMs: 60_000 })
  const collection = imports.data ? normalizeDocumentsCollection(imports.data, page, 25) : null

  const refreshImports = imports.refresh
  const processing = collection?.items.some((item) => ['QUEUED', 'PROCESSING'].includes(item.status)) ?? false
  useEffect(() => {
    if (!processing || !navigator.onLine) return
    const timer = window.setInterval(() => void refreshImports(), 5000)
    return () => window.clearInterval(timer)
  }, [processing, refreshImports])

  function updateLocation(nextPage = page, nextStatus = status, nextSearch = appliedSearch) {
    const params = new URLSearchParams()
    if (nextPage > 1) params.set('page', String(nextPage))
    if (nextStatus) params.set('status', nextStatus)
    if (nextSearch) params.set('search', nextSearch)
    setSearchParams(params, { replace: true })
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setPage(1)
    setAppliedSearch(search.trim())
    updateLocation(1, status, search.trim())
  }

  return (
    <div className="page-stack">
      <section className="page-heading"><div><p className="eyebrow">Исходные документы</p><h2>Документы и ППР</h2><p>Загружайте версии ППР, проверяйте распознанные данные и подтверждайте их для планирования работ.</p></div><Button onClick={() => setUploadOpen(true)}><Plus size={17} />Добавить документ</Button></section>
      {imports.source === 'cache' && imports.fetchedAt && <CachedNotice fetchedAt={imports.fetchedAt} stale={imports.stale} />}
      <section className="surface surface--flush">
        <form className="table-toolbar" onSubmit={submitSearch}>
          <SearchField value={search} onChange={setSearch} placeholder="Название файла или объекта" />
          <label className="compact-select"><Filter size={17} /><span className="sr-only">Статус</span><select name="importStatus" value={status} onChange={(event) => { const next = event.target.value; setStatus(next); setPage(1); updateLocation(1, next, appliedSearch) }}><option value="">Все статусы</option><option value="PROCESSING">Распознаются</option><option value="REVIEW_REQUIRED">Нужна проверка</option><option value="READY_TO_CONFIRM">Готовы к подтверждению</option><option value="CONFIRMED">Подтверждены</option><option value="FAILED">С ошибкой</option></select></label>
          <Button variant="secondary" type="submit">Найти</Button>
        </form>
        {imports.loading && <LoadingState label="Загружаем документы" />}
        {imports.error && <ErrorState error={imports.error} onRetry={() => void imports.refresh()} />}
        {collection && collection.items.length === 0 && <EmptyState icon={<FileSearch />} title="Документы не найдены" description={status || appliedSearch ? 'Измените фильтры или добавьте новый файл.' : 'Загрузите первый годовой ППР, чтобы начать проверку данных.'} action={<Button onClick={() => setUploadOpen(true)}><UploadCloud size={17} />Выбрать файл</Button>} />}
        {collection && collection.items.length > 0 && (
          <>
            <div className="responsive-table mg-imports-table">
              <table>
                <thead><tr><th>Файл</th><th>Объект / период</th><th>Результат</th><th>Замечания</th><th>Добавлен</th><th><span className="sr-only">Действие</span></th></tr></thead>
                <tbody>{collection.items.map((item) => <ImportRow key={item.id} item={item} />)}</tbody>
              </table>
            </div>
            <Pagination page={collection.meta.page} limit={collection.meta.limit} total={collection.meta.total} onChange={(next) => { setPage(next); updateLocation(next) }} />
          </>
        )}
      </section>
      <UploadModal open={uploadOpen} onClose={() => { setUploadOpen(false); if (searchParams.get('upload')) setSearchParams({}, { replace: true }) }} onUploaded={() => void imports.refresh()} />
    </div>
  )
}

function ImportRow({ item }: { item: PprDocument }) {
  const metrics = item.metrics
  return (
    <tr>
      <td data-label="Файл"><div className="table-primary"><span className="file-icon"><FileSpreadsheet size={18} /></span><span><strong>{fileName(item)}</strong><small>Версия {item.version ?? 1} · {item.sourceType || 'файл'}</small></span></div></td>
      <td data-label="Объект / период"><strong>{item.object?.name || 'Объект не указан'}</strong><small className="table-subline">{documentKindLabel(item)}</small></td>
      <td data-label="Результат"><Badge tone={statusTone(item.status)}>{importStatusLabel(item.status)}</Badge>{metrics && <small className="table-subline">{formatNumber(metrics.autoPercent, 1)}% автоматически</small>}</td>
      <td data-label="Замечания"><strong className={metrics?.blockerCount ? 'text-danger' : ''}>{formatNumber(metrics?.issueCount)}</strong><small className="table-subline">блокирующих: {formatNumber(metrics?.blockerCount)}</small></td>
      <td data-label="Добавлен">{formatDate(item.createdAt, true)}</td>
      <td><Link className="row-action" to={`/imports/${item.id}`}>Открыть</Link></td>
    </tr>
  )
}

function UploadModal({ open, onClose, onUploaded }: { open: boolean; onClose(): void; onUploaded(): void }) {
  const { user, hasAnyRole } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [year, setYear] = useState(new Date().getFullYear())
  const [objectId, setObjectId] = useState('')
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const canUpload = hasAnyRole('ROLE_ADMIN', 'ROLE_ENGINEER')
  const objects = useObjects()
  const availableObjects = objects.data || []
  const effectiveObjectId = objectId || (availableObjects.length === 1 ? availableObjects[0].id : '')

  function choose(next: File | null) {
    setError('')
    if (!next) { setFile(null); return }
    const extension = next.name.split('.').pop()?.toLowerCase() || ''
    if (!supportedExtensions.includes(extension)) { setError('Поддерживаются файлы XLSX, XLS и текстовые PDF.'); return }
    if (next.size > MAX_FILE_SIZE) { setError('Размер файла превышает 50 МБ.'); return }
    setFile(next)
  }

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragging(false)
    choose(event.dataTransfer.files[0] || null)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!file || !user) return
    setSubmitting(true)
    setError('')
    try {
      const fields: Record<string, string> = { year: String(year) }
      fields.objectId = effectiveObjectId
      const result = await executeOrQueueFile<{ data: PprDocument; meta?: { duplicate?: boolean } }>({ userId: user.id, label: `Загрузка «${file.name}»`, method: 'POST', path: '/import/ppr', file, fields, rawResponse: true })
      if (result.queued) {
        toast.show('Файл сохранён на устройстве. Отправим его после подключения.', 'success')
        onClose()
        navigate('/sync')
      } else if (result.data) {
        const payload = result.data
        const document = normalizeDocument(payload.data ?? payload)
        toast.show(payload.meta?.duplicate ? 'Этот файл уже загружен. Открываем сохранённую версию без дубликатов.' : 'Файл принят. Распознавание началось.', 'success')
        onUploaded()
        onClose()
        navigate(`/imports/${document.id}`)
      }
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Добавить годовой ППР" description="Исходный файл сохранится без изменений. Повторная загрузка того же файла не создаст дубликаты." size="medium">
      {!canUpload ? <div className="modal__body"><div className="form-message form-message--warning">Загружать ППР могут инженер и администратор.</div></div> : (
        <form onSubmit={(event) => void submit(event)}>
          <div className="modal__body form-stack">
            <label className={`drop-zone ${dragging ? 'drop-zone--active' : ''}`} onDragOver={(event) => { event.preventDefault(); setDragging(true) }} onDragLeave={() => setDragging(false)} onDrop={drop}>
              <input name="file" type="file" accept=".xlsx,.xls,.pdf,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => choose(event.target.files?.[0] || null)} />
              {file ? <><CheckCircle2 size={28} /><strong>{file.name}</strong><span>{formatNumber(file.size / 1024 / 1024, 1)} МБ · нажмите, чтобы заменить</span></> : <><UploadCloud size={30} /><strong>Перетащите файл или выберите на устройстве</strong><span>XLSX, XLS или текстовый PDF · до 50 МБ</span></>}
            </label>
            <div className="form-grid form-grid--2"><label className="field"><span>Год плана</span><input name="year" type="number" min="2000" max="2100" value={year} onChange={(event) => setYear(Number(event.target.value))} required /></label><label className="field"><span>Объект обслуживания</span><select name="objectId" value={effectiveObjectId} onChange={(event) => setObjectId(event.target.value)} required disabled={objects.loading}><option value="">Выберите объект</option>{availableObjects.map((object) => <option value={object.id} key={object.id}>{object.name}{object.code ? ` · ${object.code}` : ''}</option>)}</select></label></div>
            {objects.error && <div className="form-message form-message--warning">{objects.error.message}</div>}
            {error && <div className="form-message form-message--error" role="alert">{error}</div>}
          </div>
          <div className="modal__footer"><Button type="button" variant="quiet" onClick={onClose}>Отмена</Button><Button type="submit" loading={submitting} disabled={!file || !effectiveObjectId}>Загрузить и распознать</Button></div>
        </form>
      )}
    </Modal>
  )
}
